import { getBrowserApiBaseUrl } from "@/config/env"
import { getAuthTokenFromStorage } from "@/lib/utils/storage"
import { resolveCreditReportHistory } from "./creditReportHelpers"

/** Angular credit report iframe URL with access_token query param. */
export function buildAngularCreditReportIframeSrc(
  applicantId: string | undefined,
  accessToken?: string | null,
): string | null {
  const id = applicantId?.trim()
  if (!id) return null
  const token = (accessToken ?? getAuthTokenFromStorage())?.trim()
  if (!token) return null
  const base = getBrowserApiBaseUrl().replace(/\/$/, "")
  const q = new URLSearchParams({ access_token: token })
  return `${base}/api/credit_report/${encodeURIComponent(id)}?${q.toString()}`
}

function arrayishLen(value: unknown): number {
  if (Array.isArray(value)) return value.length
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Object.keys(value as Record<string, unknown>).length
  }
  return 0
}

/** Show Equifax HTML iframe when bureau path/score/history/plaid/ninja present (Angular ~2133). */
export function shouldShowAngularCreditReportHtmlIframe(
  applicationData: Record<string, unknown> | null | undefined,
  ninjaListLengthFromApi = 0,
  applicationAllData?: Record<string, unknown> | null | undefined,
): boolean {
  if (!applicationData) return false
  const ad = applicationData

  if (ad.equifax_credit_report_path) return true

  const plaidLen = arrayishLen(ad.plaid_accounts ?? ad.plaidAccounts ?? ad.linked_plaid)
  if (plaidLen > 0) return true

  const ninjaLen = Math.max(
    ninjaListLengthFromApi,
    arrayishLen(
      ad.ninja_accounts ?? ad.ninjaAccounts ?? ad.resident_ninja_accounts ?? ad.linked_ninja_accounts,
    ),
  )
  if (ninjaLen > 0) return true

  const nonUs = ad.non_us_address
  if (nonUs === true || nonUs === "1" || nonUs === 1) return true

  if (ad.credit_report === "1" || ad.credit_report === 1) return true

  const historyLen = resolveCreditReportHistory(ad, applicationAllData).length
  if (historyLen > 0) return true

  const es = ad.equifax_score
  // Angular `*ngIf` truthiness — string `"0"` is truthy; numeric `0` is not.
  if (es != null && es !== "" && Boolean(es)) return true
  // Bureau returned zero — still render embedded HTML (Credit Summary tab in Angular).
  if (es === 0 || es === "0") return true

  return false
}
